import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Users, Apple, ArrowRight, Loader2, X, Check, Leaf, HandHeart, Sprout, Truck } from "lucide-react";
import AIConciergeWidget from "@/components/AIConciergeWidget";
import DemoTopNav from "@/components/demos/DemoTopNav";
import { DEMO_THEMES } from "@/data/demoThemes";
import { shopDonate, shopOrderStatus } from "@/lib/api";
import { toast } from "sonner";

const GREEN = "#2F6B3A";
const DARK = "#233024";
const CREAM = "#F5F7F0";
const ORANGE = "#E2703A";
const serif = { fontFamily: "'Playfair Display', serif" };
const body = { fontFamily: "'Plus Jakarta Sans', sans-serif" };

const IMG = {
  hero: "https://images.unsplash.com/photo-1628717341663-0007b0ee2597?crop=entropy&cs=srgb&fm=jpg&w=1100&q=80&ixlib=rb-4.1.0",
  rescue: "https://images.unsplash.com/photo-1591586116988-62fe65164f8d?crop=entropy&cs=srgb&fm=jpg&w=300&q=80&ixlib=rb-4.1.0",
  mobilize: "https://images.unsplash.com/photo-1593113646773-028c64a8f1b8?crop=entropy&cs=srgb&fm=jpg&w=300&q=80&ixlib=rb-4.1.0",
  share: "https://images.unsplash.com/photo-1599059813005-11265ba4b4ce?crop=entropy&cs=srgb&fm=jpg&w=300&q=80&ixlib=rb-4.1.0",
  campaign: "https://images.unsplash.com/photo-1591586116988-62fe65164f8d?crop=entropy&cs=srgb&fm=jpg&w=400&q=80&ixlib=rb-4.1.0",
  story: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?crop=entropy&cs=srgb&fm=jpg&w=300&q=80&ixlib=rb-4.1.0",
};
const STATS = [{ n: "1.2M", l: "Meals Shared", icon: Users }, { n: "860,000", l: "Pounds Rescued", icon: Leaf }, { n: "4,500", l: "Volunteers", icon: HandHeart }];
const WAYS = [
  { icon: Heart, t: "Give Funds", d: "Fuel our mission and help more fresh food reach local families.", bg: "#E7F0E8" },
  { icon: Users, t: "Give Time", d: "Join our community of volunteers and make a hands-on difference.", bg: "#FBF3DF" },
  { icon: Apple, t: "Give Food", d: "Are you a farm, business, or grower? Donate your surplus fresh food.", bg: "#FBE7DD" },
];
const STEPS = [
  { img: IMG.rescue, t: "1. Rescue", d: "We collect surplus fresh food from farms, markets, and businesses." },
  { img: IMG.mobilize, t: "2. Mobilize", d: "Our volunteers sort, pack, and prepare food for distribution." },
  { img: IMG.share, t: "3. Share", d: "We deliver to families through local partners, food pantries, and community programs." },
];
const OPPS = [
  { t: "Produce Sorting", w: "Sat, Apr 26 · 9:00 AM – 12:00 PM" },
  { t: "Community Distribution", w: "Tue, Apr 29 · 3:00 – 6:00 PM" },
  { t: "Mobile Market Support", w: "Sat, May 3 · 9:00 AM – 1:00 PM" },
];

function DonateModal({ onClose }) {
  const [amount, setAmount] = useState(50);
  const [custom, setCustom] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const amt = custom ? parseInt(custom, 10) : amount;
    if (!amt || amt < 1) { toast.error("Please choose an amount."); return; }
    setBusy(true);
    try {
      const res = await shopDonate({ amount: amt, campaign: "Fill 10,000 Family Boxes", origin_url: window.location.origin });
      window.location.href = res.checkout_url;
    } catch { toast.error("Could not start donation. Please try again."); setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[80] bg-black/45 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6" onClick={onClose} data-testid="charity-donate-modal">
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="bg-white w-full max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()} style={body}>
        <div className="px-6 py-4 flex items-center justify-between text-white" style={{ background: GREEN }}>
          <div style={serif} className="text-lg">Support this campaign</div>
          <button onClick={onClose} data-testid="charity-donate-close"><X size={18} /></button>
        </div>
        <div className="p-6">
          <p className="text-sm text-slate-500">Help fill 10,000 family boxes this season. Every gift makes a difference.</p>
          <div className="grid grid-cols-4 gap-2 mt-4">
            {[25, 50, 100, 250].map((a) => (
              <button key={a} onClick={() => { setAmount(a); setCustom(""); }} data-testid={`charity-amount-${a}`}
                className={`rounded-lg py-3 text-sm font-semibold border transition-colors ${!custom && amount === a ? "text-white" : ""}`}
                style={!custom && amount === a ? { background: GREEN, borderColor: GREEN } : { borderColor: "#d8e0d6", color: DARK }}>${a}</button>
            ))}
          </div>
          <input value={custom} onChange={(e) => setCustom(e.target.value.replace(/\D/g, ""))} placeholder="Other amount ($)" data-testid="charity-amount-custom"
            className="w-full mt-3 rounded-lg border px-3 py-2.5 text-sm outline-none" style={{ borderColor: "#d8e0d6" }} />
          <button onClick={submit} disabled={busy} data-testid="charity-donate-submit"
            className="w-full mt-4 rounded-lg py-3.5 text-sm tracking-widest uppercase font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: ORANGE }}>
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Heart size={15} />} Donate ${custom || amount}
          </button>
          <p className="text-[11px] text-slate-400 text-center mt-2">Secure checkout powered by Stripe</p>
        </div>
      </motion.div>
    </div>
  );
}

export default function HarvestBridgeDemo() {
  const [donate, setDonate] = useState(false);
  const [thanks, setThanks] = useState(false);
  const [params, setParams] = useSearchParams();
  useEffect(() => { window.scrollTo(0, 0); document.title = "Harvest Bridge (Demo) | WebsiteTech.pro"; }, []);
  useEffect(() => {
    const sid = params.get("session_id");
    if (params.get("paid") === "1" && sid) {
      shopOrderStatus(sid).catch(() => {}).finally(() => { setThanks(true); setParams({}, { replace: true }); });
    } else if (params.get("canceled") === "1") { toast("Donation canceled."); setParams({}, { replace: true }); }
  }, [params, setParams]);

  return (
    <div className="min-h-screen" style={{ background: CREAM, color: DARK, ...body }} data-testid="charity-demo">
      <Link to="/demos" className="block w-full text-center text-[11px] tracking-widest uppercase py-1.5 text-white" style={{ background: GREEN }}>
        WebsiteTech.pro Demo · Charity / Nonprofit — click to view all industry demos
      </Link>
      <div className="text-center text-xs py-2 text-white flex items-center justify-center gap-2" style={{ background: "#7A1E1E" }}>
        <Heart size={12} /> <span className="font-semibold uppercase tracking-wide">Urgent need:</span> more volunteers for this weekend's community distribution
      </div>

      <DemoTopNav
        testid="charity-nav"
        logo={<div className="flex items-center gap-2"><Sprout size={22} style={{ color: GREEN }} /><div className="leading-tight"><div className="text-xl tracking-tight" style={{ ...serif, color: GREEN }}>HARVEST BRIDGE</div><div className="text-[8px] tracking-[0.3em] uppercase" style={{ color: ORANGE }}>Food Today · Stronger Tomorrow</div></div></div>}
        items={["Our Work", "Get Involved", "Impact", "Stories", "Resources", "About"]}
        ctaLabel="Donate"
        onCta={() => setDonate(true)}
        theme={{ bg: "rgba(245,247,240,0.94)", text: DARK, accent: ORANGE, accentText: "#fff", border: "#dde5d9" }}
      />

      {/* hero */}
      <header data-testid="charity-hero" className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-8 items-center py-12">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl leading-[1.02]" style={{ ...serif, color: DARK }}>Good food belongs<br />on every table.</h1>
          <p className="mt-5 text-lg opacity-75 max-w-md">We rescue fresh food, connect neighbors, and help communities thrive.</p>
          <div className="mt-7 flex flex-wrap gap-3">
            <button onClick={() => setDonate(true)} data-testid="charity-give-today" className="rounded-lg px-7 py-3.5 text-sm tracking-widest uppercase font-semibold text-white flex items-center gap-2" style={{ background: ORANGE }}>Give Today <ArrowRight size={15} /></button>
            <button className="rounded-lg px-7 py-3.5 text-sm tracking-widest uppercase font-semibold" style={{ border: `1px solid ${GREEN}`, color: GREEN }}>Volunteer With Us</button>
          </div>
          <div className="mt-6 flex items-center gap-2 text-sm italic" style={{ color: GREEN }}><Leaf size={15} /> People + Food + Stronger Communities</div>
        </motion.div>
        <motion.img initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }} src={IMG.hero} alt="Volunteers" className="rounded-2xl h-[360px] w-full object-cover shadow-lg" />
      </header>

      {/* impact stats */}
      <section data-testid="charity-stats" className="border-y" style={{ background: "#EAF0E6", borderColor: "#dde5d9" }}>
        <div className="max-w-7xl mx-auto px-6 py-7 flex flex-wrap items-center justify-center gap-x-12 gap-y-5">
          <span className="text-xs uppercase tracking-[0.2em] font-bold flex items-center gap-2" style={{ color: GREEN }}><Leaf size={14} /> This Year's Impact</span>
          {STATS.map((s) => (
            <div key={s.l} className="flex items-center gap-3">
              <s.icon size={22} style={{ color: GREEN }} />
              <div><div className="text-2xl font-bold" style={{ ...serif, color: DARK }}>{s.n}</div><div className="text-[10px] uppercase tracking-widest opacity-60">{s.l}</div></div>
            </div>
          ))}
        </div>
      </section>

      {/* ways to give */}
      <section data-testid="charity-ways" className="max-w-7xl mx-auto px-6 py-12 grid md:grid-cols-3 gap-4">
        {WAYS.map((w) => (
          <button key={w.t} onClick={() => w.t === "Give Funds" ? setDonate(true) : null} className="text-left rounded-xl p-6 flex items-start gap-4 hover:shadow-md transition-shadow" style={{ background: w.bg }} data-testid={`charity-way-${w.t.split(" ")[1].toLowerCase()}`}>
            <w.icon size={26} style={{ color: GREEN }} className="shrink-0 mt-1" />
            <div><div className="text-lg font-bold" style={serif}>{w.t}</div><p className="text-sm opacity-70 mt-1">{w.d}</p></div>
          </button>
        ))}
      </section>

      {/* how it works + campaign */}
      <section className="max-w-7xl mx-auto px-6 pb-14 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <span className="text-xs uppercase tracking-[0.2em] font-bold" style={{ color: GREEN }}>How Harvest Bridge Works</span>
          <div className="mt-6 grid sm:grid-cols-3 gap-6">
            {STEPS.map((s) => (
              <div key={s.t} className="text-center" data-testid="charity-step">
                <img src={s.img} alt={s.t} className="h-24 w-24 rounded-full object-cover mx-auto" />
                <div className="mt-3 font-bold" style={{ color: DARK }}>{s.t}</div>
                <p className="text-xs opacity-65 mt-1 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 grid sm:grid-cols-3 gap-4">
            <span className="text-xs uppercase tracking-[0.2em] font-bold sm:col-span-3" style={{ color: GREEN }}>Volunteer Opportunities</span>
            {OPPS.map((o) => (
              <div key={o.t} className="rounded-lg border p-3 bg-white" style={{ borderColor: "#dde5d9" }}><div className="text-sm font-semibold">{o.t}</div><div className="text-[11px] opacity-60 mt-0.5">{o.w}</div></div>
            ))}
          </div>
        </div>

        {/* campaign card */}
        <div className="rounded-2xl border bg-white p-6" style={{ borderColor: "#dde5d9" }} data-testid="charity-campaign">
          <img src={IMG.campaign} alt="" className="rounded-lg h-28 w-full object-cover" />
          <h3 className="mt-4 text-xl leading-snug" style={serif}>Help fill 10,000 family boxes this season</h3>
          <div className="mt-3 h-2.5 w-full rounded-full" style={{ background: "#E4EADF" }}><div className="h-full rounded-full" style={{ width: "68%", background: GREEN }} /></div>
          <div className="mt-1 text-xs font-semibold" style={{ color: GREEN }}>68% funded</div>
          <button onClick={() => setDonate(true)} data-testid="charity-support-campaign" className="mt-4 w-full rounded-lg py-3 text-sm tracking-widest uppercase font-semibold text-white" style={{ background: ORANGE }}>Support This Campaign</button>
          <div className="mt-3 grid grid-cols-4 gap-2">
            {[25, 50, 100, 250].map((a) => (
              <button key={a} onClick={() => setDonate(true)} className="rounded-lg border py-2 text-xs font-semibold" style={{ borderColor: "#dde5d9", color: DARK }}>${a}</button>
            ))}
          </div>
          <p className="text-[11px] text-slate-400 text-center mt-2">A healthier, stronger community is within reach.</p>
        </div>
      </section>

      {/* story + partner */}
      <section className="py-14" style={{ background: "#EAF0E6" }}>
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-10 items-center">
          <div className="flex items-center gap-5">
            <img src={IMG.story} alt="Marisol" className="h-24 w-24 rounded-full object-cover" />
            <div><p className="text-2xl leading-snug" style={serif}>"Every box carries more than food. It carries care."</p><div className="mt-2 text-sm opacity-70">— Marisol, Community Member</div></div>
          </div>
          <div className="rounded-2xl bg-white p-6 border flex items-start gap-4" style={{ borderColor: "#dde5d9" }}>
            <Truck size={26} style={{ color: GREEN }} className="shrink-0 mt-1" />
            <div>
              <div className="text-lg font-bold" style={serif}>Partner With Us</div>
              <p className="text-sm opacity-70 mt-1">Farms, businesses, schools, and community organizations make our work possible. Let's build a stronger, healthier region together.</p>
              <button className="mt-3 rounded-lg px-5 py-2.5 text-xs tracking-widest uppercase font-semibold" style={{ border: `1px solid ${GREEN}`, color: GREEN }}>Partner With Us</button>
            </div>
          </div>
        </div>
      </section>

      <footer data-testid="charity-footer" className="text-white" style={{ background: DARK }}>
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-wrap items-center justify-between gap-4 text-sm">
          <div className="flex items-center gap-2"><Sprout size={20} style={{ color: "#9BC49B" }} /><span className="tracking-tight" style={serif}>HARVEST BRIDGE</span></div>
          <p className="opacity-70">Our Work · Get Involved · Impact · Stories · Resources · About</p>
          <p className="opacity-70 italic" style={serif}>Food Today. Stronger Tomorrow.</p>
        </div>
      </footer>

      {donate && <DonateModal onClose={() => setDonate(false)} />}
      {thanks && (
        <div className="fixed inset-0 z-[90] bg-black/50 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setThanks(false)} data-testid="charity-thanks">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-2xl p-8 max-w-sm text-center" onClick={(e) => e.stopPropagation()} style={body}>
            <div className="w-14 h-14 rounded-full mx-auto grid place-items-center" style={{ background: `${GREEN}22`, color: GREEN }}><Heart size={26} /></div>
            <h3 className="mt-5 text-2xl" style={serif}>Thank you for your gift</h3>
            <p className="mt-2 text-sm text-slate-500">Your donation helps fill family boxes with fresh food. Good food, brighter tomorrows — together.</p>
            <button onClick={() => setThanks(false)} className="mt-6 rounded-lg px-8 py-3 text-xs tracking-widest uppercase font-semibold text-white" style={{ background: GREEN }}>Close</button>
          </motion.div>
        </div>
      )}
      <AIConciergeWidget demo="charity" concierge={DEMO_THEMES.charity.concierge} />
    </div>
  );
}
